import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

const typeColors: Record<ToastType, { bg: string; color: string; border: string }> = {
  success: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '#22C55E' },
  error: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '#EF4444' },
  info: { bg: 'rgba(59,130,246,0.1)', color: '#3B82F6', border: '#3B82F6' },
};

const typeIcons: Record<ToastType, string> = {
  success: '\u2713',
  error: '\u2717',
  info: '\u24D8',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const showToast = useCallback((message: string, type: ToastType) => {
    const id = ++nextId;
    setToasts(prev => [...prev, { id, message, type }]);
    const timer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      timersRef.current.delete(id);
    }, 3000);
    timersRef.current.set(id, timer);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
      }}>
        {toasts.map(toast => {
          const style = typeColors[toast.type];
          return (
            <div
              key={toast.id}
              style={{
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 20px',
                background: style.bg,
                borderLeft: `3px solid ${style.border}`,
                borderRadius: 8,
                color: style.color,
                fontSize: 13,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                minWidth: 260,
                maxWidth: 400,
                animation: 'toast-slide-in 0.2s ease-out',
              }}
              onClick={() => dismiss(toast.id)}
            >
              <span style={{ fontSize: 16 }}>{typeIcons[toast.type]}</span>
              <span style={{ flex: 1 }}>{toast.message}</span>
              <span style={{ opacity: 0.6, fontSize: 16, marginLeft: 8 }}>&times;</span>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
